import { useSyncExternalStore } from 'react';
import {
  roleLabel,
  term,
  withArticle,
  type GenderStyle,
  type TermKey,
} from '@flm9/i18n/labels';
import { api } from './api';

// Club setting `locale.gender_style`, fetched once per page load (public,
// no login needed). Until it arrives — or offline — the neutral form is used.
const STORAGE_KEY = 'flm9.genderStyle';
let style: GenderStyle = readStored();
const listeners = new Set<() => void>();
let requested = false;

function readStored(): GenderStyle {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'masculine' ? 'masculine' : 'neutral';
  } catch {
    return 'neutral';
  }
}

function load() {
  if (requested) return;
  requested = true;
  api
    .wording()
    .then((w) => {
      const next: GenderStyle = w?.genderStyle === 'masculine' ? 'masculine' : 'neutral';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Storage unavailable (private mode) — in-memory value still applies.
      }
      if (next !== style) {
        style = next;
        listeners.forEach((l) => l());
      }
    })
    .catch(() => {
      // Offline / server down: keep the stored or neutral style; retry next load.
      requested = false;
    });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  load();
  return () => listeners.delete(cb);
}

/** Current gender style for non-React code paths. */
export function currentGenderStyle(): GenderStyle {
  return style;
}

/**
 * Person terms in the club's gender style:
 *   const t = useTerm();
 *   t('rangeOfficer')                  // "Standführer:in" | "Standführer"
 *   t('shooter', { plural: true })     // "Schütz:innen" | "Schützen"
 *   t.article('rangeOfficer', 'indef') // "eine Standführer:in" | "ein Standführer"
 */
export function useTerm() {
  const s = useSyncExternalStore(subscribe, () => style);
  const t = (key: TermKey, opts?: { plural?: boolean }) => term(key, s, opts);
  return Object.assign(t, {
    style: s,
    article: (key: TermKey, kind: 'def' | 'indef' = 'def') => withArticle(key, s, kind),
    role: (role: Parameters<typeof roleLabel>[0]) => roleLabel(role, s),
  });
}
